import React, { useEffect, useState } from 'react';
import {
  Box,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/react';
import {
  IoIosHeart,
  IoMdCard,
  IoMdCart,
  IoMdLock,
  IoMdLogOut,
  IoMdPerson,
  IoMdNotifications,
  IoMdTrophy,
} from 'react-icons/io';
import { FaStar } from 'react-icons/fa';
import axios from 'axios';

import {
  NavLink,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Informazioni from './Account/Informazioni';
import Tesseramenti from './Account/Tesseramenti';
import Ordini from './Account/Ordini';
import TuoiPremi from './Account/TuoiPremi';
import Sicurezza from './Account/Sicurezza';
import Notifiche from './Account/Notifiche';
import Bazar from './Account/Bazar';
import { useAuth } from '../contexts/AuthContext';
import Subscriptions from './Account/Subscriptions';
import CILplus from '../pages/CILplus';
import Achievements from '../pages/Achievements';
import NetworkProfiles from './Account/NetworkProfiles';
import { getPendingNotificationsCount as getPendingNotificationsCountFromUser } from '../utils/pendingNotifications';

const getAccountNavItems = (userData) =>
  [
    {
      to: '/account/informazioni',
      label: 'Account',
      icon: IoMdPerson,
    },
    {
      to: '/account/ordini',
      label: 'Ordini',
      icon: IoMdCart,
    },
    {
      to: '/account/tuoi-premi',
      label: 'I tuoi premi',
      icon: IoMdTrophy,
    },
    {
      to: '/account/notifiche',
      label: 'Notifiche',
      icon: IoMdNotifications,
      showNotificationBadge: true,
    },
    {
      to: '/account/subscriptions',
      label: 'Donazioni',
      icon: IoIosHeart,
    },
    {
      to: '/account/achievements',
      label: 'Achievement',
      icon: IoMdTrophy,
      beta: true,
      showIf: (user) => user?.permissions?.includes('beta-tester'),
    },
    {
      to: '/account/tesseramenti',
      label: 'Tesseramenti',
      icon: IoMdCard,
    },
    {
      to: '/account/cilplus',
      label: 'CIL+',
      icon: FaStar,
      cilPlus: true,
      showIf: (user) => user?.subscriptionStatus === 'active',
    },
    {
      to: '/account/sicurezza',
      label: 'Sicurezza',
      icon: IoMdLock,
    },
  ].filter((item) => !item.showIf || item.showIf(userData));

const ACCOUNT_MENU_THEME = {
  bg: '#274559',
  hover: '#2f5268',
  border: 'rgba(54, 169, 225, 0.35)',
  shadow: '0 4px 20px rgba(15, 35, 50, 0.4)',
  shadowSidebar: '0 20px 40px rgba(15, 35, 50, 0.45)',
  labelMuted: '#9ec9de',
  textMuted: '#b8d4e3',
  footerMuted: '#7aa8bc',
  navHover: 'rgba(54, 169, 225, 0.18)',
  navBorder: 'rgba(54, 169, 225, 0.28)',
};

const UserProfile = ({ isAuthenticated, onAuth }) => {
  const { userData, loading, error, requestUserData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuIndex, setMobileMenuIndex] = useState(-1);

  // Helper function to check if a route is active
  const isRouteActive = (path) => {
    return location.pathname === path;
  };

  // Helper function to calculate pending notifications
  const getPendingNotificationsCount = () =>
    getPendingNotificationsCountFromUser(userData);

  // For theming / color modes

  useEffect(() => {
    requestUserData();
  }, []);

  useEffect(() => {
    setMobileMenuIndex(-1);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      const response = await axios.delete('/api/logout');
      if (response.status === 200) {
        onAuth(false);
      }
    } catch (logoutError) {
      console.error('Logout Error', logoutError);
    }
  };

  // 1) If user is not authenticated, redirect immediately to homepage
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // 2) Handle loading and error states (only if isAuthenticated === true)
  if (loading) {
    return (
      <Box textAlign="center" mt={10} width="100%">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={10}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  // 3) If fetch is complete but user is null, render nothing or handle accordingly
  if (!userData) {
    return null;
  }

  const navItems = getAccountNavItems(userData);
  const activeNavLabel =
    navItems.find((item) => isRouteActive(item.to))?.label ?? 'Menu';
  const pendingCount = getPendingNotificationsCount();

  const closeMobileMenu = () => setMobileMenuIndex(-1);

  const renderNavButton = (item, onNavigate) => {
    const {
      to,
      label,
      icon: Icon,
      cilPlus,
      beta,
      showNotificationBadge,
    } = item;
    const active = isRouteActive(to);

    const handleClick = onNavigate
      ? () => {
          onNavigate();
          navigate(to);
        }
      : undefined;

    const buttonProps = cilPlus
      ? {
          bg: active ? '#36a9e1' : 'transparent',
          color: active ? 'white' : '#ffcc00',
          border: active ? 'none' : '2px solid #3b235a',
          _hover: {
            bg: active ? '#2f95c7' : '#3b235a',
            color: 'white',
            transform: 'translateX(4px)',
            borderColor: '#ffcc00',
          },
        }
      : {
          bg: active ? '#36a9e1' : 'transparent',
          color: active ? 'white' : 'gray.300',
          border: active ? 'none' : `1px solid ${ACCOUNT_MENU_THEME.navBorder}`,
          _hover: {
            bg: active ? '#2f95c7' : ACCOUNT_MENU_THEME.navHover,
            color: 'white',
            transform: 'translateX(4px)',
            borderColor: '#ffcc00',
          },
        };

    return (
      <Button
        key={to}
        as={onNavigate ? 'button' : NavLink}
        to={onNavigate ? undefined : to}
        type={onNavigate ? 'button' : undefined}
        borderRadius="12px"
        fontWeight="600"
        h="48px"
        justifyContent="flex-start"
        px={4}
        _active={{ transform: 'scale(.98)' }}
        transition="all 0.3s ease"
        leftIcon={<Icon />}
        onClick={handleClick}
        position={showNotificationBadge ? 'relative' : undefined}
        {...buttonProps}
      >
        {beta ? (
          <HStack spacing={2} align="center">
            <Text>{label}</Text>
            <Badge
              bg="#ff6b35"
              color="white"
              fontSize="2xs"
              fontWeight="700"
              px={1.5}
              py={0.5}
              borderRadius="4px"
              textTransform="uppercase"
              letterSpacing="0.5px"
            >
              BETA
            </Badge>
          </HStack>
        ) : (
          label
        )}
        {showNotificationBadge && pendingCount > 0 && (
          <Badge
            position="absolute"
            top="50%"
            right="-2px"
            transform="translateY(-50%)"
            colorScheme="red"
            variant="solid"
            borderRadius="full"
            minW="18px"
            h="18px"
            fontSize="xs"
            fontWeight="bold"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 2px 4px rgba(0,0,0,0.2)"
          >
            {pendingCount}
          </Badge>
        )}
      </Button>
    );
  };

  const renderAccountNav = (onNavigate) => (
    <VStack spacing={3} align="stretch">
      {navItems.map((item) => renderNavButton(item, onNavigate))}
      <Button
        bg="transparent"
        color="#e73341"
        border="1px solid #e73341"
        borderRadius="12px"
        fontWeight="600"
        h="48px"
        justifyContent="flex-start"
        px={4}
        onClick={() => {
          handleLogout();
          onNavigate?.();
        }}
        data-testid="logout-button"
        _hover={{
          bg: '#e73341',
          color: 'white',
          transform: 'translateX(4px)',
          borderColor: '#d62d3a',
        }}
        _active={{ transform: 'scale(.98)' }}
        transition="all 0.3s ease"
        leftIcon={<IoMdLogOut />}
      >
        Esci dal sito
      </Button>
    </VStack>
  );

  return (
    <Flex
      minH="100vh"
      direction={{ base: 'column', md: 'row' }}
      bg="#36393f"
      gap={{ base: 0, md: 4 }}
      p={{ base: 0, md: 4 }}
    >
      {/* SIDEBAR - visible on md+ */}
      <Box
        w={{ base: 'full', md: '280px' }}
        bg={ACCOUNT_MENU_THEME.bg}
        borderRadius="16px"
        border="1px solid"
        borderColor={ACCOUNT_MENU_THEME.border}
        p={5}
        display={{ base: 'none', md: 'block' }}
        boxShadow={ACCOUNT_MENU_THEME.shadowSidebar}
        position="sticky"
        top="16px"
        maxH="calc(100vh - 32px)"
        overflowY="auto"
      >
        {/* Sidebar Header */}
        <VStack as="header" spacing={1} mb={6} textAlign="center">
          <Text fontSize="lg" fontWeight="bold" color="white" mb={1}>
            Area Personale
          </Text>
          <Text fontSize="sm" color={ACCOUNT_MENU_THEME.textMuted}>
            Benvenutə, {userData?.firstName || 'Utente'}
          </Text>
        </VStack>

        {/* Sidebar Menu Items */}
        {renderAccountNav()}

        {/* Sidebar Footer */}
        <VStack
          as="footer"
          mt={6}
          pt={4}
          spacing={1}
          borderTop="1px solid"
          borderColor={ACCOUNT_MENU_THEME.border}
        >
          <Text
            fontSize="xs"
            color={ACCOUNT_MENU_THEME.labelMuted}
            textAlign="center"
            mb={2}
          >
            CIL - Creatività in Libertà
          </Text>
          <Text
            fontSize="xs"
            color={ACCOUNT_MENU_THEME.footerMuted}
            textAlign="center"
          >
            Versione 2.0
          </Text>
        </VStack>
      </Box>

      {/* MOBILE MENU - accordion, visible on base only */}
      <Box
        position="fixed"
        top="56px"
        left="0"
        right="0"
        zIndex="1000"
        display={{ base: 'block', md: 'none' }}
        bg={ACCOUNT_MENU_THEME.bg}
        borderBottom="1px solid"
        borderColor={ACCOUNT_MENU_THEME.border}
        boxShadow={ACCOUNT_MENU_THEME.shadow}
      >
        <Accordion
          allowToggle
          index={mobileMenuIndex}
          onChange={(index) => setMobileMenuIndex(index)}
          reduceMotion
        >
          <AccordionItem border="none">
            <AccordionButton
              px={4}
              py={3}
              bg={ACCOUNT_MENU_THEME.bg}
              _hover={{ bg: ACCOUNT_MENU_THEME.hover }}
              _expanded={{ bg: ACCOUNT_MENU_THEME.hover }}
            >
              <Box flex="1" textAlign="left">
                <Text
                  fontSize="xs"
                  color={ACCOUNT_MENU_THEME.labelMuted}
                  mb={0.5}
                >
                  Area Personale
                </Text>
                <Text fontWeight="bold" color="white">
                  {activeNavLabel}
                </Text>
              </Box>
              {pendingCount > 0 && (
                <Badge
                  mr={2}
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  minW="20px"
                  h="20px"
                  fontSize="xs"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {pendingCount}
                </Badge>
              )}
              <AccordionIcon color="white" />
            </AccordionButton>
            <AccordionPanel
              px={3}
              pb={4}
              pt={0}
              maxH="70vh"
              overflowY="auto"
              bg={ACCOUNT_MENU_THEME.bg}
            >
              {renderAccountNav(closeMobileMenu)}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>

      {/* MAIN CONTENT AREA */}
      <Box
        flex="1"
        p={{ base: 3, md: 4 }}
        pt={{ base: '86px', md: 4 }} // Extra top padding on mobile to clear the fixed menu
        bg="transparent"
      >
        <Routes>
          <Route index element={<Navigate to="informazioni" replace />} />
          <Route
            path="informazioni"
            element={
              <Informazioni
                user={userData}
                handleLogout={handleLogout}
                onUserDataUpdate={requestUserData}
              />
            }
          />
          <Route
            path="tesseramenti"
            element={<Tesseramenti tesseramenti={userData.tesseramenti} />}
          />
          <Route
            path="ordini"
            element={
              <Ordini userData={userData} requestUserData={requestUserData} />
            }
          />
          <Route
            path="tuoi-premi"
            element={<TuoiPremi userData={userData} />}
          />
          <Route path="bazar" element={<Bazar userData={userData} />} />
          <Route path="network" element={<NetworkProfiles />} />
          <Route path="notifiche" element={<Notifiche userData={userData} />} />
          <Route
            path="subscriptions"
            element={
              <Subscriptions
                userData={userData}
                requestUserData={requestUserData}
              />
            }
          />
          <Route path="achievements" element={<Achievements />} />
          <Route path="cilplus" element={<CILplus />} />
          <Route path="sicurezza" element={<Sicurezza user={userData} />} />
        </Routes>
      </Box>
    </Flex>
  );
};

export default UserProfile;
